import { StoreThunk } from './types';
import { User, Role } from '../types';
import getAuthModule from '../auth';

const authModule = getAuthModule();

export const SET_USERS: 'SET_USERS' = 'SET_USERS';
export const SET_ROLES: 'SET_ROLES' = 'SET_ROLES';
export const SET_USER_ERROR: 'SET_USER_ERROR' = 'SET_USER_ERROR';
export const ADD_USER: 'ADD_USER' = 'ADD_USER';
export const DELETE_USER: 'DELETE_USER' = 'DELETE_USER';
export const UPDATE_USER: 'UPDATE_USER' = 'UPDATE_USER';

/**
 * Add a new user.
 */
export const addUser = (user: User): AddUser => ({
  type: ADD_USER,
  user
});

/**
 * Delete a user.
 */
export const deleteUser = (userId: string): DeleteUser => ({
  type: DELETE_USER,
  userId
});

/**
 * Update a user.
 */
export const updateUser = (user: User): UpdateUser => ({
  type: UPDATE_USER,
  user
});

/**
 * Replace all existing users.
 */
export const setUsers = (users: User[] = []): SetUsers => ({
  type: SET_USERS,
  users
});

/**
 * Set all of the available roles.
 */
export const setRoles = (roles: Role[] = []): SetRoles => ({
  type: SET_ROLES,
  roles
});

/**
 * Persist any errors encountered when updating user data.
 */
export const setUserError = (error?: string): SetUserError => ({
  type: SET_USER_ERROR,
  error
});

export interface SetUsers {
  type: typeof SET_USERS;
  users: User[];
}

export interface SetRoles {
  type: typeof SET_ROLES;
  roles: Role[];
}

export interface SetUserError {
  type: typeof SET_USER_ERROR;
  error?: string;
}

export interface AddUser {
  type: typeof ADD_USER;
  user: User;
}

export interface DeleteUser {
  type: typeof DELETE_USER;
  userId: string;
}

export interface UpdateUser {
  type: typeof UPDATE_USER;
  user: User;
}

/**
 * Retrieve the users from the user pool, marshal the data into the
 * appropriate format, and save them in the app store.
 */
export function fetchUsers(): StoreThunk<Promise<SetUsers | void>> {
  return (dispatch, getState) => {
    // Don't fetch users if we've already fetched
    if (getState().users != null) {
      return Promise.resolve();
    }

    return authModule
      .getUsers()
      .then(users =>
        dispatch(
          setUsers(
            // tslint:disable-next-line:no-any
            users.map(({ user, groups }) => {
              const u: User = {
                user_id: user.Username!,
                status: user.UserStatus!,
                roles: groups,
                disabled: !user.Enabled
              };
              for (const attr of user.Attributes!) {
                switch (attr.Name) {
                  case 'email':
                    u.email = attr.Value;
                    break;
                  case 'email_verified':
                    u.email_verified = attr.Value;
                    break;
                  default:
                }
              }
              return u;
            })
          )
        )
      )
      .catch(error => {
        dispatch(setUserError(error));
        return dispatch(setUsers([]));
      });
  };
}

/**
 * Retrieves the available roles.
 */
export function fetchRoles(): StoreThunk<Promise<SetRoles | void>> {
  return (dispatch, getState) => {
    // Don't fetch roles if we've already fetched
    if (getState().roles != null) {
      return Promise.resolve();
    }

    return authModule
      .getGroups()
      .then(groups =>
        dispatch(
          setRoles(<Role[]>groups.map(group => ({
            name: group.GroupName!,
            applicationId: group.UserPoolId!,
            description: group.Description!
          })))
        )
      )
      .catch(error => {
        dispatch(setUserError(error));
        return dispatch(setRoles([]));
      });
  };
}

/**
 * Deletes a user from the authorization service and updates the local
 * store to reflect that.
 */
export function deleteUserFromService(
  userId: string
): StoreThunk<Promise<DeleteUser | SetUserError>> {
  return dispatch => {
    return authModule
      .deleteUser(userId)
      .then(() => dispatch(deleteUser(userId)))
      .catch(error => dispatch(setUserError(error)));
  };
}

/**
 * Adds a new user to the authorization service and the local store.
 */
export function addUserToService(
  email: string,
  role: string
): StoreThunk<Promise<AddUser | SetUserError>> {
  return dispatch => {
    return authModule
      .addUser(email, role)
      .then(({ user, groups }) => {
        let _email: string | undefined;
        let _emailVerified: string | undefined;

        for (const attr of user.Attributes!) {
          switch (attr.Name) {
            case 'email':
              _email = attr.Value;
              break;
            case 'email_verified':
              _emailVerified = attr.Value;
              break;
            default:
              break;
          }
        }

        return dispatch(
          addUser({
            user_id: user.Username!,
            email: _email,
            email_verified: _emailVerified,
            roles: groups
          })
        );
      })
      .catch(error => dispatch(setUserError(error)));
  };
}

export function enableUser(userId: string): StoreThunk<Promise<UpdateUser>> {
  return dispatch => {
    return authModule.enableUser(userId).then(() => {
      return dispatch(
        updateUser({
          user_id: userId,
          disabled: false
        })
      );
    });
  };
}



// WEBPACK FOOTER //
// ./src/actions/user.ts